import {
  Box,
  Flex,
  Grid,
  GridItem,
  IconButton,
  Popover,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Tooltip,
  VisuallyHidden,
  useDisclosure,
} from '@chakra-ui/react';
import { ReactComponent as ShapeSwapIcon } from 'assets/icons/a11ymenu_shape.svg';
import { ReactComponent as FillIcon } from 'assets/icons/drop.svg';
import { ReactComponent as HorizontalMirrorIcon } from 'assets/icons/mirror_horizontal.svg';
import { ReactComponent as VerticalMirrorIcon } from 'assets/icons/mirror_vertical.svg';
import { SHAPE_CREATED, SHAPE_REPLACED } from 'constants/mixpanel';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { ColorPicker } from 'modules/Editor/components/ColorPicker';
import { AltTextMenu } from 'modules/Editor/components/Toolbar/AltTextMenu/AltTextMenu';
import {
  BorderRadiusPopover,
  BorderStyle,
  BorderStyleSelect,
  BorderWidthPopover,
} from 'modules/common/components/Border';
import { ToolbarDivider } from 'modules/common/components/Toolbar/ToolbarDivider';
import { SliderPopover } from 'modules/common/components/ToolbarPopover';
import React, { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useEditor, useWidget } from 'widgets/sdk';

import {
  BASIC_SHAPE_WIDGET_ITEMS,
  DEFAULT_BORDER_WIDTH,
  DEFAULT_SECONDARY_FILL_COLOR,
  FLEXIBLE_RATIO_SHAPES,
} from './BasicShapeWidget.config';
import { checkIsActive } from './BasicShapeWidget.helpers';
import { BasicShapeType, BasicShapeWidgetData } from './BasicShapeWidget.types';

export const BasicShapeWidgetToolbarMenu = (): ReactElement => {
  const { widgetId, type } = useWidget<BasicShapeWidgetData>();
  return (
    <Flex gap='2' align='center' data-testid='basic-shape-widget-toolbar'>
      <Fill />
      <ToolbarDivider />
      <ShapeSwap />
      <ToolbarDivider />
      <MirrorOptions />
      <ToolbarDivider />

      <BorderWidthOption />
      <BorderStyleOption />
      {type === BasicShapeType.Rectangle && <BorderRadiusOption />}

      <ToolbarDivider />

      <AltTextMenu widgetId={widgetId} />
    </Flex>
  );
};

const Fill = () => {
  const { t } = useTranslation('editor_widget_toolbar', { useSuspense: false, keyPrefix: 'shapesToolbar' });
  const { fillColor, fillPercent, updateWidget } = useWidget<BasicShapeWidgetData>();

  const onUpdatePrimaryFillColor = (newFillColor: string): void => {
    const widgetFillColor = { fillColor: [newFillColor] };
    if (fillColor[1]) widgetFillColor.fillColor.push(fillColor[1]);

    updateWidget(widgetFillColor);
  };

  const onUpdateSecondaryFillColor = (newFillColor: string): void => {
    const widgetFillColor = { fillColor: [fillColor[0], newFillColor] };
    updateWidget(widgetFillColor);
  };

  const onUpdateFillPercent = (newFillPercent: number) => {
    const widgetData: { fillPercent: number; fillColor?: string[] } = { fillPercent: newFillPercent };
    if (fillPercent === 0 && fillColor.length === 1) {
      widgetData.fillColor = [...fillColor, DEFAULT_SECONDARY_FILL_COLOR];
    }

    updateWidget(widgetData);
  };

  const hasSecondaryColorFill = fillColor[1] && fillPercent && fillPercent > 0 && fillPercent <= 100;
  const fillColorLabel = hasSecondaryColorFill ? t('fillColor.one') : t('fillColor.label');
  const secondaryFillLabel = t('fillColor.two');

  return (
    <>
      <ColorPicker color={fillColor[0]} onChange={onUpdatePrimaryFillColor} label={fillColorLabel} />
      {hasSecondaryColorFill ? (
        <ColorPicker color={fillColor[1]} onChange={onUpdateSecondaryFillColor} label={secondaryFillLabel} />
      ) : null}
      <BorderColorOption />
      <FillPercentOption value={fillPercent || 0} onChange={onUpdateFillPercent} />
    </>
  );
};

const MirrorOptions = () => {
  const { t } = useTranslation('editor_widget_toolbar', { useSuspense: false, keyPrefix: 'shapesToolbar' });
  const { updateWidget, mirror = {} } = useWidget<BasicShapeWidgetData>();

  const onUpdateMirrorVertical = (): void => updateWidget({ mirror: { isVertical: !mirror.isVertical } });
  const onUpdateMirrorHorizontal = (): void => updateWidget({ mirror: { isHorizontal: !mirror.isHorizontal } });

  const verticalLabel = t('mirror.flipVertical');
  const horizontalLabel = t('mirror.flipHorizontal');

  return (
    <>
      <Tooltip hasArrow placement='bottom' label={horizontalLabel} bg='black'>
        <IconButton
          aria-label={horizontalLabel}
          icon={<HorizontalMirrorIcon />}
          size={'sm'}
          onClick={onUpdateMirrorHorizontal}
          variant={'ghost'}
        />
      </Tooltip>

      <Tooltip hasArrow placement='bottom' label={verticalLabel} bg='black'>
        <IconButton
          aria-label={verticalLabel}
          icon={<VerticalMirrorIcon />}
          size={'sm'}
          onClick={onUpdateMirrorVertical}
          variant={'ghost'}
        />
      </Tooltip>
    </>
  );
};

const BorderColorOption = () => {
  const { t } = useTranslation('editor_widget_toolbar', { useSuspense: false, keyPrefix: 'shapesToolbar' });
  const {
    border: { color, width },
    updateWidget,
  } = useWidget<BasicShapeWidgetData>();

  const onUpdateBorderColor = (borderColor: string): void => {
    const newBorder = {
      color: borderColor,
      ...(width === 0 && { width: DEFAULT_BORDER_WIDTH }),
    };
    updateWidget({ border: newBorder });
  };

  return <ColorPicker color={color} onChange={onUpdateBorderColor} label={t('border.color')} iconStyle={'border'} />;
};

const BorderStyleOption = () => {
  const {
    border: { style },
    updateWidget,
  } = useWidget<BasicShapeWidgetData>();

  const onUpdateBorderStyle = (style: BorderStyle) => updateWidget({ border: { style: style || BorderStyle.Solid } });

  return <BorderStyleSelect onChange={onUpdateBorderStyle} value={style} />;
};

const BorderWidthOption = () => {
  const {
    border: { width },
    updateWidget,
  } = useWidget<BasicShapeWidgetData>();

  const onUpdateBorderWidth = (borderWidth: number) => updateWidget({ border: { width: borderWidth } });

  return <BorderWidthPopover value={width} onChange={onUpdateBorderWidth} />;
};

const BorderRadiusOption = () => {
  const { cornerRadius, updateWidget } = useWidget<BasicShapeWidgetData>();

  const onUpdateBorderRadius = (cornerRadius: number) => updateWidget({ cornerRadius });

  return <BorderRadiusPopover value={cornerRadius} onChange={onUpdateBorderRadius} />;
};

const FillPercentOption = ({ value, onChange }: { value: number; onChange: (f: number) => void }) => {
  const { t } = useTranslation('editor_widget_toolbar', { useSuspense: false, keyPrefix: 'shapesToolbar' });
  const label = t('colorFill');

  return (
    <SliderPopover
      value={value}
      icon={<FillIcon />}
      label={label}
      onChange={onChange}
      suffix={'%'}
      max={100}
      title={label}
    />
  );
};

const ShapeSwap = () => {
  const { t } = useTranslation('editor_widget_toolbar', { useSuspense: false, keyPrefix: 'shapesToolbar' });
  const { isOpen, onClose, onToggle } = useDisclosure();
  const label = t('shapeSwap.ariaLabel');

  return (
    <Popover isOpen={isOpen} onClose={onClose} isLazy closeOnBlur closeOnEsc>
      <Tooltip hasArrow placement='bottom' label={t('shapeSwap.tooltip')} bg='black'>
        <Box>
          <PopoverTrigger>
            <IconButton
              variant={'ghost-with-active'}
              aria-label={label}
              size={'sm'}
              icon={<ShapeSwapIcon width={'1.2rem'} height={'1.2rem'} />}
              isActive={isOpen}
              onClick={onToggle}
            />
          </PopoverTrigger>
        </Box>
      </Tooltip>
      <PopoverContent p={3} color='font.500' w='fit-content'>
        <VisuallyHidden>
          <PopoverHeader>{label}</PopoverHeader>
        </VisuallyHidden>
        <ShapePopoverContent />
      </PopoverContent>
    </Popover>
  );
};

const ShapePopoverContent = () => {
  const { boundingBox } = useEditor();
  const { type: formerType, cornerRadius, widthPx, heightPx, updateWidget } = useWidget<BasicShapeWidgetData>();

  const replaceWidget = (key: string): void => {
    const defaultData = BASIC_SHAPE_WIDGET_ITEMS[BASIC_SHAPE_WIDGET_ITEMS.findIndex((item) => item.key === key)];
    const { type: newType, iconDimensions, label, options } = defaultData;

    let updateWidgetData = { type: newType, ...options };
    if (!(FLEXIBLE_RATIO_SHAPES.includes(formerType) && FLEXIBLE_RATIO_SHAPES.includes(newType)))
      updateWidgetData.heightPx = widthPx / (iconDimensions.widthPx / iconDimensions.heightPx);

    updateWidget(updateWidgetData);
    setTimeout(() => boundingBox.updateRect(), 0);

    Mixpanel.track(SHAPE_REPLACED.eventName, {
      preset: label,
      shape_id: formerType,
      from: SHAPE_CREATED.from.Replace,
    });
  };

  return (
    <Grid templateColumns='repeat(4, minmax(0, 1fr))' gap={1}>
      {BASIC_SHAPE_WIDGET_ITEMS.map(({ key, type, label, icon }, index) => {
        if (key === 'square' || key === 'rectangle' || key === 'leaf-right')
          return <React.Fragment key={`fragment-${index}`} />;
        return (
          <GridItem key={`grid-${index}`}>
            <IconButton
              variant={'ghost-with-active'}
              aria-label={`Add ${label}`}
              size={'sm'}
              key={`icon-${index}`}
              isActive={checkIsActive({ key, formerType, type, widthPx, heightPx, cornerRadius })}
              icon={icon}
              onClick={() => replaceWidget(key)}
            />
          </GridItem>
        );
      })}
    </Grid>
  );
};
